import { showConfirm } from '../components/ConfirmDialog';
import AirportInput from '../components/AirportInput';
import AirlineCompanyField from '../components/AirlineCompanyField';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apiGetTeamMember, apiCreateTeamMember, apiUpdateTeamMember,
  apiCreatePassport, apiUpdatePassport, apiDeletePassport, apiValidateMrz, docScanUrl, apiUploadDocScan,
  apiCreateVisa, apiUpdateVisa, apiDeleteVisa,
  apiCreateAirline, apiUpdateAirline, apiDeleteAirline,
  apiGetBands, apiLinkTeamMemberBand, apiUnlinkTeamMemberBand,
  type TeamMemberDTO, type PassportDTO, type VisaDTO, type AirlineDTO, type BandDTO,
  type TeamRole,
} from '../api';
import { useProfileContext } from '../hooks/useProfileContext';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import CollapsibleSection from '../components/CollapsibleSection';
import PageHeader from '../components/PageHeader';
import CountryInput from '../components/CountryInput';
import AutoTextarea from '../components/AutoTextarea';
import DocScanField, { buildScanDataUrl } from '../components/DocScanField';
import { useSuggestionList } from '../hooks/useSuggestionList';
import { useWriteGate } from '../lib/writeGate';

const TAG_OPTIONS = ['musician', 'crew'];

// PERSONAL fields (2026-10-07, decision C): withheld by the server from anyone
// who is not an owner / superadmin / the person themselves. When a card comes
// back `restricted`, these inputs are FROZEN instead of showing an empty value —
// the server also preserves the stored values on save, so a blind edit can only
// not blank them.
const RESTRICTED_FIELDS = new Set([
  'email', 'phone', 'address', 'date_of_birth', 'nationality',
  'emergency_contact_name', 'emergency_contact_phone',
  'real_name', 'last_name', 'post_code', 'locality', 'country',
  'notes', 'frequent_flyer', 'airline_program', 'loyalty_program',
  'default_airport',
]);
// NOTE: `diet` / `diet_notes` / `non_food_allergies` are deliberately NOT here —
// they are CATERING data the rider PDF needs, so a band editor keeps reading and
// editing them (user decision, 2026-10-07).

function emptyMember(): Partial<TeamMemberDTO> {
  return { name: '', category: 'crew', role: '', base_role: 'main', email: '', phone: '', address: '', date_of_birth: '', notes: '',
    frequent_flyer: '', airline_program: '', loyalty_program: '', nationality: '', emergency_contact_name: '', emergency_contact_phone: '',
    default_airport: '',
    nickname: '', real_name: '', last_name: '', post_code: '', locality: '', country: '', diet: '', diet_notes: '', non_food_allergies: '' };
}

export default function TeamEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeProfileId } = useProfileContext();

  // Tolerate a missing param: the /new route has none, so `id === 'new'` alone
  // would be false and the page would hang on "Loading…".
  const isNew = !id || id === 'new';

  const [member, setMember] = useState<Partial<TeamMemberDTO>>(emptyMember());

  // ---- Capability-aware write gate (lot A1, 2026-09-25) ----
  // A `read-only` GLOBAL account (users.role) whose BAND role is editor/owner got
  // the full editor here while the server refused every write (403 → red
  // « Not saved »). The gate freezes the controls and short-circuits every write
  // path below.
  //
  // BAND RESOLUTION (debt of A1, closed by lot A2): the gate used to be resolved
  // from `activeProfileId` alone. A team member is linked to one or more bands and
  // the page is reached from a global list (`/tour/team/:id` carries no band), so
  // a member of band X edited while band Y is open was decided on Y — coarse, and
  // wrong whenever X ≠ Y. We now prefer the ACTIVE band when the member belongs to
  // it (strictly unchanged behaviour in the common case) and fall back to the
  // member's OWN band otherwise.
  const memberBandId =
    (member.bands || []).find((b) => b.id === activeProfileId)?.id
    || member.bands?.[0]?.id
    || activeProfileId;
  const writeGate = useWriteGate(memberBandId);
  const canWrite = writeGate.canWrite;
  // Snapshot of the last persisted member (server round-trip), used to skip the
  // debounced autosave when nothing changed since load/save — prevents a
  // redundant PUT right after load and any wipe-on-load-error (2026-08-26).
  const lastSavedRef = useRef<string>('');
  const { types: dietTypes } = useSuggestionList('diet-types');
  const [passports, setPassports] = useState<PassportDTO[]>([]);
  // Passports section open state (2026-10-21): open by default, and the
  // auto-expand below re-opens it whenever a passport carries a scan — the
  // scan + Auto-fill button sit at the TOP of the card, never folded away.
  const [passportsOpen, setPassportsOpen] = useState(true);
  const [visas, setVisas] = useState<VisaDTO[]>([]);
  const [airlines, setAirlines] = useState<AirlineDTO[]>([]);
  const [loaded, setLoaded] = useState(isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // ---- Band associations ----
  const [allBands, setAllBands] = useState<BandDTO[]>([]);
  const [newBandId, setNewBandId] = useState('');
  const [newBandRole, setNewBandRole] = useState('crew');
  const [linkingBand, setLinkingBand] = useState(false);

  useEffect(() => {
    if (isNew) { setLoaded(true); return; }
    let cancelled = false;
    apiGetTeamMember(id!)
      .then((m) => {
        if (cancelled) return;
        setMember(m);
        setPassports(m.passports || []);
        // Auto-expand the Passports section when a scan exists (2026-10-21):
        // the user's scan + Auto-fill button must never sit folded away.
        if ((m.passports || []).some((p) => p.has_scan || p.scan_path)) setPassportsOpen(true);
        setVisas(m.visas || []);
        setAirlines(m.airlines || []);
        setLoaded(true);
        lastSavedRef.current = JSON.stringify(m);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load');
        setLoaded(true);
        // Do NOT clear lastSavedRef here: on a transient 401/500 we must not
        // autosave the (possibly empty) state over the real data (2026-08-26).
      });
    return () => { cancelled = true; };
  }, [id, isNew]);

  // ---- Autosave (debounced) ----
  // Editing any field on the Team page (including allergies) persists WITHOUT
  // the Save button. PUT /api/team/:id also triggers the server-side
  // syncAllergiesToRider, so the Musicians/Crew rider cards stay in sync with
  // the Team page (source of truth). (2026-08-26)
  useEffect(() => {
    if (isNew || !member.name?.trim() || !canWrite) return;
    if (JSON.stringify(member) === lastSavedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const saved = await apiUpdateTeamMember(id!, member);
        lastSavedRef.current = JSON.stringify(saved);
        reportSaveOk('team');
      } catch (err) {
        reportSaveError('team', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [member, isNew, id, canWrite]);

  // Load the list of bands the user may link this person to.
  useEffect(() => {
    if (isNew) return;
    apiGetBands().then(setAllBands).catch(() => setAllBands([]));
  }, [isNew]);

  const restricted = !isNew && !!member.restricted;
  const field = (k: keyof TeamMemberDTO) => ({
    value: (member[k] as string) || '',
    // Frozen for a global read-only account (lot A1) — the server 403s the write.
    // Also frozen when the PERSONAL data of this card is restricted (2026-10-07).
    disabled: !canWrite || (restricted && RESTRICTED_FIELDS.has(String(k))),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setMember((p) => ({ ...p, [k]: e.target.value })),
  });

  const save = useCallback(async () => {
    if (!canWrite) return;
    if (!member.name?.trim()) { window.alert(t('teamEdit.nameRequired')); return; }
    setSaving(true);
    try {
      if (isNew) {
        const created = await apiCreateTeamMember({
          ...member,
          band_id: activeProfileId || undefined,
        });
        reportSaveOk('team');
        navigate(`/tour/team/${created.id}`, { replace: true });
      } else {
        await apiUpdateTeamMember(id!, member);
        reportSaveOk('team');
        navigate('/tour/team');
      }
    } catch (err) {
      reportSaveError('team', err);
    } finally {
      setSaving(false);
    }
  }, [member, isNew, id, activeProfileId, navigate, canWrite]);

  // ---- Band association handlers ----
  const linkBand = async () => {
    if (isNew || !newBandId || !canWrite) return;
    setLinkingBand(true);
    try {
      const updated = await apiLinkTeamMemberBand(id!, newBandId, newBandRole);
      setMember(updated);
      setNewBandId('');
    } catch (err) {
      reportSaveError('team', err);
    } finally {
      setLinkingBand(false);
    }
  };
  const unlinkBand = async (bandId: string) => {
    if (!canWrite) return;
    const band = (member.bands || []).find((b) => b.id === bandId);
    if (!(await showConfirm(t('teamEdit.unlinkConfirm', { name: band?.name || bandId })))) return;
    try {
      await apiUnlinkTeamMemberBand(id!, bandId);
      setMember((p) => ({ ...p, bands: (p.bands || []).filter((b) => b.id !== bandId) }));
    } catch (err) {
      reportSaveError('team', err);
    }
  };
  const linkedBandIds = new Set((member.bands || []).map((b) => b.id));
  const availableBands = allBands.filter((b) => !linkedBandIds.has(b.id));

  // Per-band roles (foh, mon, lights, guitar tech…) live as TAGS on the band's
  // rider cards (Musicians/Crew pages), not on the Team identity (2026-08-07).
  // The band link itself carries only the legacy role mirror.

  // ---- Passport handlers ----
  const addPassport = async () => {
    if (isNew || !canWrite) return;
    try {
      const p = await apiCreatePassport(id!, { passport_number: '', nationality: '', expiry_date: '' });
      setPassports((prev) => [...prev, p]);
    } catch (err) { reportSaveError('team', err); }
  };
  const patchPassport = async (docId: string, patch: Partial<PassportDTO>) => {
    if (!canWrite) return;
    // Merge inside the functional updater and save the MERGED document: several
    // patchPassport calls fired in the same tick (e.g. a multi-field draft
    // apply) must each build on the previous ones — measured bug (2026-10-21):
    // the last PUT carried a stale base and DROPPED the fields written by the
    // earlier calls of the same batch.
    setPassports((prev) => {
      const next = prev.map((p) => (p.id === docId ? { ...p, ...patch } : p));
      const current = next.find((p) => p.id === docId);
      void (async () => {
        try {
          await apiUpdatePassport(id!, docId, { ...current } as PassportDTO);
          reportSaveOk('team');
        } catch (err) { reportSaveError('team', err); }
      })();
      return next;
    });
  };
  const removePassport = async (docId: string) => {
    if (!canWrite) return;
    if (!(await showConfirm(t('teamEdit.deletePassportConfirm')))) return;
    try {
      await apiDeletePassport(id!, docId);
      setPassports((prev) => prev.filter((p) => p.id !== docId));
    } catch (err) { reportSaveError('team', err); }
  };

  // ---- Passport OCR auto-fill (option B, 2026-10-21) ----
  // The extraction is a PROPOSAL held in LOCAL state (ocrDraft): the card
  // inputs show the proposed values with a visible "auto-filled — please
  // verify" notice, and nothing reaches the DB until the user applies them
  // through the EXISTING save path (patchPassport -> apiUpdatePassport).
  // Only EMPTY card fields are pre-filled; a manual edit of a drafted field
  // drops the draft for that field (the typed value wins, never overwritten).
  type OcrDraft = { method: 'mrz' | 'vision'; fields: Partial<Record<'passport_number' | 'nationality' | 'issuing_country' | 'expiry_date' | 'issue_date', string>> };
  const [ocrDraft, setOcrDraft] = useState<Record<string, OcrDraft | undefined>>({});
  const [ocrBusy, setOcrBusy] = useState<Record<string, boolean>>({});
  const [ocrMsg, setOcrMsg] = useState<Record<string, string>>({});

  const passportValue = (p: PassportDTO, key: 'passport_number' | 'nationality' | 'issuing_country' | 'expiry_date' | 'issue_date'): string => {
    const draft = ocrDraft[p.id]?.fields[key];
    return (draft !== undefined && draft !== null ? draft : (p[key] || ''));
  };
  const clearDraftField = (docId: string, key: string) => {
    setOcrDraft((prev) => {
      const d = prev[docId];
      if (!d) return prev;
      const fields = { ...d.fields } as Record<string, unknown>;
      delete fields[key];
      return { ...prev, [docId]: { ...d, fields: fields as OcrDraft['fields'] } };
    });
  };
  const patchPassportField = (p: PassportDTO, key: 'passport_number' | 'nationality' | 'issuing_country' | 'expiry_date' | 'issue_date', value: string) => {
    if (ocrDraft[p.id]?.fields[key] !== undefined) clearDraftField(p.id, key);
    patchPassport(p.id, { [key]: value } as Partial<PassportDTO>);
  };
  const autoFillPassport = async (p: PassportDTO) => {
    setOcrBusy((prev) => ({ ...prev, [p.id]: true }));
    setOcrMsg((prev) => ({ ...prev, [p.id]: '' }));
    try {
      // OPTION B (pivot confidentialité): the OCR runs ENTIRELY in this
      // browser (tesseract.js, lazy-loaded). The image bytes come from the
      // local disk choice OR the guarded /scan route and NEVER leave the
      // machine — only the two raw MRZ text lines are sent to our server
      // for local check-digit validation. No API fallback: an unreadable
      // MRZ just asks the user to fill manually.
      const res = await fetch(docScanUrl('passport', p.id, { memberId: member.id || id || '' }), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('scan_unavailable');
      const blob = await res.blob();
      const { extractMrzLines } = await import('../lib/mrzOcr');
      const lines = await extractMrzLines(blob);
      const r = await apiValidateMrz(id!, p.id, lines[0] || '', lines[1] || '');
      if (r.ok && r.proposal) {
        // Pre-fill EMPTY fields only — never overwrite a typed value.
        // issue_date is an ESTIMATE (expiry − 10 y / − 5 y under 15), NOT an
        // MRZ extraction: it is labeled "estimated" on the field.
        const fields: OcrDraft['fields'] = {};
        for (const key of ['passport_number', 'nationality', 'issuing_country', 'expiry_date', 'issue_date'] as const) {
          const proposed = String(r.proposal[key] || '').trim();
          const existing = String(p[key] || '').trim();
          if (proposed && !existing) fields[key] = proposed;
        }
        if (Object.keys(fields).length === 0) {
          setOcrMsg((prev) => ({ ...prev, [p.id]: t('teamEdit.autoFillNoEmpty') }));
        } else {
          setOcrDraft((prev) => ({ ...prev, [p.id]: { method: 'mrz', fields } }));
        }
      } else {
        setOcrMsg((prev) => ({ ...prev, [p.id]: r.error || t('teamEdit.autoFillFail') }));
      }
    } catch {
      setOcrMsg((prev) => ({ ...prev, [p.id]: t('teamEdit.autoFillFail') }));
    } finally {
      setOcrBusy((prev) => ({ ...prev, [p.id]: false }));
    }
  };
  const applyPassportDraft = (p: PassportDTO) => {
    const draft = ocrDraft[p.id];
    if (!draft) return;
    // ONE merged patch — one PUT for the whole draft (never one per field:
    // the per-field loop was racy and dropped earlier fields, measured
    // 2026-10-21 on the real scan E2E).
    const patch: Partial<PassportDTO> = {};
    for (const [key, value] of Object.entries(draft.fields)) {
      if (value !== undefined && value !== null) (patch as Record<string, unknown>)[key] = value;
    }
    patchPassport(p.id, patch);
    setOcrDraft((prev) => ({ ...prev, [p.id]: undefined }));
    setOcrMsg((prev) => ({ ...prev, [p.id]: '' }));
  };
  const discardPassportDraft = (docId: string) => {
    setOcrDraft((prev) => ({ ...prev, [docId]: undefined }));
    setOcrMsg((prev) => ({ ...prev, [docId]: '' }));
  };

  // ---- Passport DIRECT upload (empty state, 2026-10-21) ----
  // One gesture for the user who "just wants to add their passport": picking a
  // file (or taking a photo on mobile) a) CREATES the passport document with
  // default empty fields, b) UPLOADS the scan through the existing guarded
  // scan route, c) launches the MRZ auto-fill (option B, local OCR) right
  // away. When a passport already exists, the card keeps its historical
  // behaviour (scan at the top + manual Auto-fill button). The image still
  // never leaves the machine for the OCR: only the two MRZ text lines are
  // validated server-side.
  const directScanInputRef = useRef<HTMLInputElement | null>(null);
  const directCaptureInputRef = useRef<HTMLInputElement | null>(null);
  const [directBusy, setDirectBusy] = useState(false);
  const directUploadPassport = async (file: File | undefined) => {
    if (!file || isNew || !canWrite || !id) return;
    setDirectBusy(true);
    try {
      const p = await apiCreatePassport(id, { passport_number: '', nationality: '', expiry_date: '' });
      const dataUrl = await buildScanDataUrl(file);
      const res = await apiUploadDocScan('passport', p.id, { dataUrl }, { memberId: member.id || id });
      const np: PassportDTO = { ...p, scan_path: res.scan_path || '', has_scan: true };
      setPassports((prev) => [...prev, np]);
      reportSaveOk('team');
      await autoFillPassport(np);
    } catch (err) { reportSaveError('team', err); }
    finally { setDirectBusy(false); }
  };

  // ---- Visa handlers ----
  const addVisa = async () => {
    if (isNew || !canWrite) return;
    try {
      const v = await apiCreateVisa(id!, { country: '', visa_type: '', expiry_date: '', entry_type: 'single' });
      setVisas((prev) => [...prev, v]);
    } catch (err) { reportSaveError('team', err); }
  };
  const patchVisa = async (docId: string, patch: Partial<VisaDTO>) => {
    if (!canWrite) return;
    setVisas((prev) => prev.map((v) => (v.id === docId ? { ...v, ...patch } : v)));
    try {
      const current = visas.find((v) => v.id === docId);
      await apiUpdateVisa(id!, docId, { ...current, ...patch });
      reportSaveOk('team');
    } catch (err) { reportSaveError('team', err); }
  };
  const removeVisa = async (docId: string) => {
    if (!canWrite) return;
    if (!(await showConfirm(t('teamEdit.deleteVisaConfirm')))) return;
    try {
      await apiDeleteVisa(id!, docId);
      setVisas((prev) => prev.filter((v) => v.id !== docId));
    } catch (err) { reportSaveError('team', err); }
  };

  // ---- Airline / loyalty-program handlers (multi-airline collection) ----
  const addAirline = async () => {
    if (isNew || !canWrite) return;
    try {
      const a = await apiCreateAirline(id!, { company: '', program_number: '', tier: '' });
      setAirlines((prev) => [...prev, a]);
    } catch (err) { reportSaveError('team', err); }
  };
  const patchAirline = async (docId: string, patch: Partial<AirlineDTO>) => {
    if (!canWrite) return;
    setAirlines((prev) => prev.map((a) => (a.id === docId ? { ...a, ...patch } : a)));
    try {
      const current = airlines.find((a) => a.id === docId);
      await apiUpdateAirline(id!, docId, { ...current, ...patch });
      reportSaveOk('team');
    } catch (err) { reportSaveError('team', err); }
  };
  const removeAirline = async (docId: string) => {
    if (!canWrite) return;
    if (!(await showConfirm(t('teamEdit.deleteLoyaltyConfirm')))) return;
    try {
      await apiDeleteAirline(id!, docId);
      setAirlines((prev) => prev.filter((a) => a.id !== docId));
    } catch (err) { reportSaveError('team', err); }
  };

  if (!loaded) return <div className="page"><p>{t('teamEdit.loading')}</p></div>;

  return (
    <div className="page" data-edit-page="team" data-density="socle">
      <PageHeader
        title={isNew ? t('teamEdit.newPerson') : t('teamEdit.editTitle', { name: member.name || t('teamEdit.unnamed') })}
        actions={
          <>
            <button className="btn btn-secondary" data-edit-back onClick={() => navigate('/tour/team')}>{t('teamEdit.back')}</button>
            <button className="btn btn-primary" data-edit-save onClick={save} disabled={saving || !canWrite}>
              {saving ? t('teamEdit.saving') : (isNew ? t('teamEdit.create') : t('teamEdit.save'))}
            </button>
          </>
        }
      />

      {/* ONE discreet note (lot A1) — same key everywhere, existing conventions
          (dimmed small text, no new colour). The showcase (demo) is explained by
          the app-wide <ShowcaseBanner/>, so it never adds a local note here. */}
      {writeGate.readOnlyReason === 'global-read-only' && !canWrite && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', marginTop: 0 }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--danger)', marginBottom: 18 }}>
          <strong style={{ color: 'var(--danger)' }}>{t('teamEdit.failedLoad')}</strong>
          <p style={{ color: 'var(--text-dim)', margin: '6px 0 0' }}>{loadError}</p>
        </div>
      )}

      <div className="musician-form">
        <CollapsibleSection title={t('teamEdit.identity')} defaultOpen>
          {restricted && (
            <div className="card" style={{ borderColor: 'var(--info)', marginBottom: 12, padding: 12 }}>
              <strong>{t('teamEdit.restrictedTitle')}</strong>
              <p style={{ color: 'var(--text-dim)', margin: '6px 0 0', fontSize: '0.88rem' }}>
                {t('teamEdit.restrictedNote')}
              </p>
            </div>
          )}
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.nickname')}</label>
              <input className="form-input" {...field('nickname')} placeholder={t('teamEdit.stageNamePh')} />
            </div>
          </div>
          <div className="form-group">
            <label>{t('teamEdit.tags')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {TAG_OPTIONS.map((tag) => {
                // Category is a first-class field on the Team identity
                // (team_members.category). The musician/crew selector edits it
                // directly; those values are NOT stored as tags (2026-08-23).
                const active = member.category === tag;
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`rc-pill${active ? ' on' : ''}`}
                    disabled={!canWrite}
                    onClick={() => {
                      setMember((p) => ({
                        ...p,
                        category: tag as TeamMemberDTO['category'],
                        // Drop any legacy musician/crew category tags and keep
                        // only role tags.
                        tags: (p.tags || []).filter((x) => x !== 'musician' && x !== 'crew'),
                      }));
                    }}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            <small style={{ color: 'var(--text-dim)' }}>
              {t('teamEdit.tagsHint')}
            </small>
          </div>
          <div className="form-group">
            <label>{t('teamEdit.baseRole')}</label>
            <select
              className="form-input"
              value={member.base_role || 'main'}
              disabled={!canWrite}
              onChange={(e) => setMember((p) => ({ ...p, base_role: e.target.value }))}
            >
              {member.category === 'musician' ? (
                <>
                  <option value="main">{t('teamEdit.mainPermanent')}</option>
                  <option value="session">{t('teamEdit.sessionDeputy')}</option>
                  <option value="guest">{t('teamEdit.guest')}</option>
                </>
              ) : (
                <>
                  <option value="main">{t('teamEdit.mainPermanent2')}</option>
                  <option value="session">{t('teamEdit.sessionDeputy')}</option>
                </>
              )}
            </select>
            <small style={{ color: 'var(--text-dim)' }}>
              {t('teamEdit.baseRoleHint')}
            </small>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.dateOfBirth')}</label>
              <input className="form-input" type="date" {...field('date_of_birth')} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.nationality')}</label>
              <CountryInput value={member.nationality || ''} onChange={(v) => setMember((p) => ({ ...p, nationality: v }))} placeholder={t('teamEdit.nationalityPh')} disabled={!canWrite || restricted} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.usualName')}</label>
              <input className="form-input" data-edit-field="name" {...field('name')} placeholder={t('teamEdit.nameUsedInBand')} />
              <small style={{ color: 'var(--text-dim)' }}>{t('teamEdit.displayNameHint')}</small>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.legalFirstName')}</label>
              <input className="form-input" {...field('real_name')} placeholder={t('teamEdit.asOnPassport')} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.legalLastName')}</label>
              <input className="form-input" {...field('last_name')} placeholder={t('teamEdit.asOnPassport')} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.phone')}</label>
              <input className="form-input" {...field('phone')} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.email')}</label>
              <input className="form-input" type="text" {...field('email')} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.address')}</label>
              <input className="form-input" {...field('address')} />
            </div>
            {/* Aéroport de préférence (2026-10-17) : OVERRIDE celui du groupe — proposé
                par défaut comme origine des vols du membre (feuille de travel day
                personnalisée : chaque personne peut venir d'endroits différents). */}
            <div className="form-group" style={{ flex: 1 }}>
              <label>
                {t('teamEdit.defaultAirport')}
                {' '}
                <small style={{ opacity: 0.7 }}>(IATA / UIC)</small>
              </label>
              <AirportInput
                mode={/^\d{5,8}$/.test(String(member.default_airport || '').trim()) ? 'rail' : 'air'}
                value={(member.default_airport as string) || ''}
                disabled={!canWrite || (restricted && RESTRICTED_FIELDS.has('default_airport'))}
                placeholder={t('teamEdit.defaultAirportPh')}
                onChange={(v) => setMember((p) => ({ ...p, default_airport: v }))}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.postCode')}</label>
              <input className="form-input" {...field('post_code')} placeholder={t('teamEdit.postalCodePh')} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.locality')}</label>
              <input className="form-input" {...field('locality')} placeholder={t('teamEdit.cityPh')} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.country')}</label>
              <CountryInput value={member.country || ''} onChange={(v) => setMember((p) => ({ ...p, country: v }))} placeholder={t('teamEdit.countryPh')} disabled={!canWrite || restricted} />
            </div>
          </div>
          <div className="form-group">
            <label>{t('teamEdit.notes')}</label>
            <AutoTextarea className="form-input" rows={3} {...field('notes')} />
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.diet')}</label>
              <input className="form-input" list="diet-types-suggestions" {...field('diet')} placeholder={t('teamEdit.dietPh')} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.dietDetails')}</label>
              <input className="form-input" {...field('diet_notes')} placeholder={t('teamEdit.dietDetailsPh')} />
            </div>
          </div>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('teamEdit.nonFoodAllergies')}</label>
              <AutoTextarea
                className="form-input"
                rows={2}
                {...field('non_food_allergies')}
                placeholder={t('teamEdit.nonFoodAllergiesPh')}
              />
            </div>
          </div>
        </CollapsibleSection>

        {isNew ? (
          <p style={{ color: 'var(--text-dim)', marginTop: 16 }}>
            {t('teamEdit.saveFirstHint')}
          </p>
        ) : (
          <>
            <CollapsibleSection title={t('teamEdit.bandAssociations', { count: (member.bands || []).length })}>
              {(member.bands || []).length === 0 && (
                <p style={{ color: 'var(--text-dim)' }}>{t('teamEdit.noBandAssociated')}</p>
              )}
              {(member.bands || []).map((b) => {
                const roles = b.roles?.length ? b.roles : (b.link_role ? [b.link_role] : []);
                return (
                  <div key={b.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>🎵 <strong>{b.name}</strong></span>
                      <button className="btn btn-small btn-danger" data-edit-delete onClick={() => unlinkBand(b.id)} disabled={!canWrite}>{t('teamEdit.removeLink')}</button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {roles.length === 0 ? (
                        <em style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)' }}>{t('teamEdit.noRole')}</em>
                      ) : (
                        roles.map((r) => (
                          <span
                            key={`${b.id}:${r}`}
                            className="band-role-chip rc-chip"
                          >
                            {r}
                          </span>
                        ))
                      )}
                    </div>
                    <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 6px' }} data-card-kind={b.card_kind || 'none'}>
                      {b.card_kind
                        ? t('teamEdit.cardKind', { kind: b.card_kind })
                        : t('teamEdit.noCardYet')}
                    </p>
                    <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 8 }}>
                      {t('teamEdit.rolesOnBandPage')}
                    </p>
                  </div>
                );
              })}
              <div className="form-row rc-form-row" style={{ alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>{t('teamEdit.addBand')}</label>
                  <select
                    className="form-input"
                    value={newBandId}
                    disabled={!canWrite}
                    onChange={(e) => setNewBandId(e.target.value)}
                  >
                    <option value="">{t('teamEdit.chooseBand')}</option>
                    {availableBands.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  {availableBands.length === 0 && (
                    <small style={{ color: 'var(--text-dim)' }}>{t('teamEdit.allBandsAssociated')}</small>
                  )}
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('teamEdit.roleInBand')}</label>
                  <select className="form-input" value={newBandRole} disabled={!canWrite} onChange={(e) => setNewBandRole(e.target.value as TeamRole)}>
                    {['musician', 'crew'].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary" onClick={linkBand} disabled={!newBandId || linkingBand || !canWrite}>
                  {linkingBand ? t('teamEdit.linking') : t('teamEdit.associate')}
                </button>
              </div>
            </CollapsibleSection>

            {!restricted && (
            <CollapsibleSection
              title={t('teamEdit.passports', { count: passports.length })}
              /* Controlled open state (2026-10-21): the section opens by
                 default AND auto-expands whenever a passport carries a scan —
                 the scan + Auto-fill button live at the TOP of the card, there
                 is no point leaving them folded away. */
              open={passportsOpen}
              onToggle={setPassportsOpen}
              headerActions={<button className="btn btn-primary btn-small" onClick={addPassport} disabled={!canWrite}>{t('teamEdit.addPassport')}</button>}
            >
              {passports.length === 0 && (
                canWrite ? (
                  /* DIRECT UPLOAD ZONE (empty state, 2026-10-21): the user who
                     "just wants to put their passport" gets ONE gesture — the
                     file choice creates the document, uploads the scan and
                     launches the MRZ auto-fill. No document form needed first. */
                  <div data-passport-direct-upload style={{ margin: '4px 0 12px' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button className="btn btn-primary" disabled={directBusy}
                        onClick={() => directScanInputRef.current?.click()}>
                        {directBusy ? t('teamEdit.directUploadBusy') : t('teamEdit.uploadPassportScan')}
                      </button>
                      <button className="btn btn-secondary" disabled={directBusy}
                        onClick={() => directCaptureInputRef.current?.click()}>
                        {t('teamEdit.takePassportPhoto')}
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: 4, color: 'var(--text-dim)' }}>
                      {t('teamEdit.directUploadHint')}
                    </small>
                    <input ref={directScanInputRef} type="file"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void directUploadPassport(f); }} />
                    <input ref={directCaptureInputRef} type="file" accept="image/*" capture="environment"
                      style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void directUploadPassport(f); }} />
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-dim)' }}>{t('teamEdit.noPassport')}</p>
                )
              )}
              {passports.map((p) => (
                <div key={p.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                  {/* PASSPORT SCAN AT THE TOP of the card (option B, 2026-10-21):
                      encrypted at rest, readable by an owner / superadmin / the
                      person themselves (2026-10-07). The scan drives the
                      auto-fill (MRZ-first Mistral vision + local check digits);
                      the extraction is a PROPOSAL — nothing is written until
                      the user applies it. */}
                  <DocScanField
                    kind="passport"
                    docId={p.id}
                    memberId={member.id || id || ''}
                    scanPath={p.scan_path}
                    hasScan={p.has_scan}
                    canEdit={canWrite}
                    onChanged={(sp) => patchPassport(p.id, { scan_path: sp })}
                  />
                  {canWrite && (p.scan_path || p.has_scan) && (
                    <div style={{ margin: '6px 0 10px' }}>
                      <button className="btn btn-secondary btn-small" data-passport-ocr
                        disabled={!!ocrBusy[p.id]}
                        onClick={() => autoFillPassport(p)}>
                        {ocrBusy[p.id] ? t('teamEdit.autoFillBusy') : t('teamEdit.autoFillScan')}
                      </button>
                      {ocrMsg[p.id] && (
                        <small style={{ display: 'block', marginTop: 4, color: 'var(--text-dim)' }}>{ocrMsg[p.id]}</small>
                      )}
                      {ocrDraft[p.id] && (
                        <div style={{ marginTop: 6, padding: '6px 8px', border: '1px solid var(--border, #ccc)', borderRadius: 6 }}>
                          <small style={{ color: 'var(--text-dim)' }}>
                            {t('teamEdit.autoFilledBadge', { method: ocrDraft[p.id]!.method === 'mrz' ? 'MRZ' : 'OCR' })}
                          </small>
                          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <button className="btn btn-primary btn-small" onClick={() => applyPassportDraft(p)}>{t('teamEdit.applyAutoFill')}</button>
                            <button className="btn btn-secondary btn-small" onClick={() => discardPassportDraft(p.id)}>{t('teamEdit.discardAutoFill')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.passportNumber')}{ocrDraft[p.id]?.fields.passport_number !== undefined ? ` ⚡` : ''}</label>
                      <input className="form-input" value={passportValue(p, 'passport_number')}
                        disabled={!canWrite}
                        onChange={(e) => patchPassportField(p, 'passport_number', e.target.value)} />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.nationality')}{ocrDraft[p.id]?.fields.nationality !== undefined ? ` ⚡` : ''}</label>
                      <CountryInput value={passportValue(p, 'nationality')}
                        onChange={(v) => patchPassportField(p, 'nationality', v)} disabled={!canWrite} />
                    </div>
                  </div>
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.issuingCountry')}{ocrDraft[p.id]?.fields.issuing_country !== undefined ? ` ⚡` : ''}</label>
                      <CountryInput value={passportValue(p, 'issuing_country')}
                        onChange={(v) => patchPassportField(p, 'issuing_country', v)} disabled={!canWrite} />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.issueDate')}{ocrDraft[p.id]?.fields.issue_date !== undefined ? ` ⚡ · ${t('teamEdit.autoFillEstimated')}` : ''}</label>
                      <input className="form-input" type="date" value={passportValue(p, 'issue_date').slice(0, 10)}
                        disabled={!canWrite}
                        onChange={(e) => patchPassportField(p, 'issue_date', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.expiryDate')}{ocrDraft[p.id]?.fields.expiry_date !== undefined ? ` ⚡` : ''}</label>
                      <input className="form-input" type="date" value={passportValue(p, 'expiry_date').slice(0, 10)}
                        disabled={!canWrite}
                        onChange={(e) => patchPassportField(p, 'expiry_date', e.target.value)} />
                    </div>
                  </div>
                  <button className="btn btn-small btn-danger" data-edit-delete onClick={() => removePassport(p.id)} disabled={!canWrite}>{t('teamEdit.delete')}</button>
                </div>
              ))}
            </CollapsibleSection>
            )}

            {!restricted && (
            <CollapsibleSection
              title={t('teamEdit.visas', { count: visas.length })}
              headerActions={<button className="btn btn-primary btn-small" onClick={addVisa} disabled={!canWrite}>{t('teamEdit.addVisa')}</button>}
            >
              {visas.length === 0 && <p style={{ color: 'var(--text-dim)' }}>{t('teamEdit.noVisa')}</p>}
              {visas.map((v) => (
                <div key={v.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.country')}</label>
                      <CountryInput value={v.country || ''}
                        onChange={(val) => patchVisa(v.id, { country: val })} disabled={!canWrite} />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.type')}</label>
                      <input className="form-input" value={v.visa_type || ''}
                        disabled={!canWrite}
                        onChange={(e) => patchVisa(v.id, { visa_type: e.target.value })} placeholder={t('teamEdit.typePh')} />
                    </div>
                  </div>
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.number')}</label>
                      <input className="form-input" value={v.visa_number || ''}
                        disabled={!canWrite}
                        onChange={(e) => patchVisa(v.id, { visa_number: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.entries')}</label>
                      <select className="form-input" value={v.entry_type || 'single'}
                        disabled={!canWrite}
                        onChange={(e) => patchVisa(v.id, { entry_type: e.target.value as 'single' | 'multiple' })}>
                        <option value="single">{t('teamEdit.single')}</option>
                        <option value="multiple">{t('teamEdit.multiple')}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.expiryDate')}</label>
                      <input className="form-input" type="date" value={(v.expiry_date || '').slice(0, 10)}
                        disabled={!canWrite}
                        onChange={(e) => patchVisa(v.id, { expiry_date: e.target.value })} />
                    </div>
                  </div>
                  {/* Identity-document SCAN (image or multi-page PDF). */}
                  <DocScanField
                    kind="visa"
                    docId={v.id}
                    memberId={member.id || id || ''}
                    scanPath={v.scan_path}
                    hasScan={v.has_scan}
                    canEdit={canWrite}
                    onChanged={(sp) => patchVisa(v.id, { scan_path: sp })}
                  />
                  <button className="btn btn-small btn-danger" data-edit-delete onClick={() => removeVisa(v.id)} disabled={!canWrite}>{t('teamEdit.delete')}</button>
                </div>
              ))}
            </CollapsibleSection>
            )}

            {!restricted && (
            <CollapsibleSection
              title={t('teamEdit.loyaltyPrograms', { count: airlines.length })}
              headerActions={<button className="btn btn-primary btn-small" onClick={addAirline} disabled={!canWrite}>{t('teamEdit.add')}</button>}
            >
              {airlines.length === 0 && <p style={{ color: 'var(--text-dim)' }}>{t('teamEdit.noLoyalty')}</p>}
              {airlines.map((a) => (
                <div key={a.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.company')}</label>
                      <AirlineCompanyField
                        value={a.company || ''}
                        onChange={(v) => patchAirline(a.id, { company: v })}
                        onAlliance={(al) => patchAirline(a.id, { alliance: al })}
                        datalistId={`airline-sug-${a.id}`}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.alliance')}</label>
                      <input className="form-input" value={a.alliance || ''}
                        disabled={!canWrite}
                        onChange={(e) => patchAirline(a.id, { alliance: e.target.value })}
                        placeholder={t('teamEdit.alliancePh')} />
                    </div>
                  </div>
                  <div className="form-row rc-form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.programNumber')}</label>
                      <input className="form-input" value={a.program_number || ''}
                        disabled={!canWrite}
                        onChange={(e) => patchAirline(a.id, { program_number: e.target.value })} placeholder={t('teamEdit.ffpPh')} />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('teamEdit.statusTier')}</label>
                      <input className="form-input" value={a.tier || ''}
                        disabled={!canWrite}
                        onChange={(e) => patchAirline(a.id, { tier: e.target.value })} placeholder={t('teamEdit.tierPh')} />
                    </div>
                  </div>
                  <button className="btn btn-small btn-danger" data-edit-delete onClick={() => removeAirline(a.id)} disabled={!canWrite}>{t('teamEdit.delete')}</button>
                </div>
              ))}
            </CollapsibleSection>
            )}
          </>
        )}
      </div>
      {/* datalist must live outside any grid */}
      <datalist id="diet-types-suggestions">
        {dietTypes.map((d) => <option key={d} value={d} />)}
      </datalist>
    </div>
  );
}